import type { WebappAttribution } from './attribution.types';

const ATTRIBUTION_KEY = 'webapp:attribution';

// Read campaign params from a query string into the backend's attribution shape.
function parseAttribution(search: string): WebappAttribution {
  const params = new URLSearchParams(search);
  const attribution: WebappAttribution = {};

  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');
  const utmContent = params.get('utm_content');
  const utmTerm = params.get('utm_term');
  const ref = params.get('ref');

  if (utmSource) attribution.utmSource = utmSource;
  if (utmMedium) attribution.utmMedium = utmMedium;
  if (utmCampaign) attribution.utmCampaign = utmCampaign;
  if (utmContent) attribution.utmContent = utmContent;
  if (utmTerm) attribution.utmTerm = utmTerm;
  if (ref) attribution.referralCode = ref;

  return attribution;
}

/**
 * Capture campaign params from the current URL and persist them to
 * localStorage. Called as early as possible on load — before the Google OAuth
 * popup round-trip can drop the query string. Only writes when at least one
 * param is present, so it never clobbers a previously stored value with empties.
 */
export function captureAttributionFromUrl(): void {
  if (typeof window === 'undefined') return;

  const attribution = parseAttribution(window.location.search);
  if (Object.keys(attribution).length === 0) return;

  try {
    localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch {
    // Storage unavailable (private mode / quota) — attribution is best-effort.
  }
}

/** Read the stored attribution, or undefined when none / unparseable. */
export function getStoredAttribution(): WebappAttribution | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = localStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as WebappAttribution;
    return Object.keys(parsed).length > 0 ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/** Clear stored attribution after it has been successfully forwarded. */
export function clearStoredAttribution(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(ATTRIBUTION_KEY);
  } catch {
    // ignore
  }
}
